import { useMemo, useState, useCallback } from "react";
import i18n from "i18next";
import {
  extractFontsFromState,
  generateGoogleFontsLink,
  checkFontsAvailability,
  isSupportedGoogleFont,
  isLocalFont,
} from "../../common/font_extractor.es6";

const buildFontHelpers = ({ getPluginToolbars, getState, setState }) => {
  const loadFonts = async () => {
    const state = {
      pluginToolbarsById: getPluginToolbars(),
    };
    const usedFonts = extractFontsFromState(state);

    if (usedFonts.size === 0) {
      return;
    }

    const fontNames = Array.from(usedFonts.keys());
    const fontAvailability = await checkFontsAvailability(fontNames);
    const missingFonts = fontNames.filter((fontName) => !fontAvailability.get(fontName));

    if (missingFonts.length === 0) {
      return;
    }

    const supportedGoogleFonts = [];
    const localFonts = [];
    const unknownFonts = [];

    const { failedFontLinks = [] } = getState();

    missingFonts.forEach((fontName) => {
      if (isLocalFont(fontName)) {
        localFonts.push(fontName);
      } else if (isSupportedGoogleFont(fontName)) {
        supportedGoogleFonts.push(fontName);
      } else {
        if (!failedFontLinks.includes(fontName)) {
          unknownFonts.push(fontName);
        }
      }
    });

    if (supportedGoogleFonts.length > 0) {
      const supportedFontsMap = new Map();
      supportedGoogleFonts.forEach((fontName) => {
        supportedFontsMap.set(fontName, usedFonts.get(fontName));
      });

      const googleFontsLink = generateGoogleFontsLink(supportedFontsMap);

      if (googleFontsLink) {
        const linkUrl = googleFontsLink.match(/href=["']([^"']+)["']/)?.[1];

        if (linkUrl) {
          const existingLinks = document.querySelectorAll(
            'link[href*="fonts.googleapis.com/css2"]'
          );
          const linkExists = Array.from(existingLinks).some((link) => link.href === linkUrl);

          if (!linkExists) {
            const linkElement = document.createElement("link");
            linkElement.rel = "stylesheet";
            linkElement.href = linkUrl;
            document.head.appendChild(linkElement);
          }
        }
      }
    }

    if (unknownFonts.length > 0) {
      setState({
        fontLinkModalVisible: true,
        pendingFontLinks: unknownFonts,
        fontLinkModalResult: null,
      });
    }
  };

  const handleFontLinkModalConfirm = async () => {
    setState({ fontLinkModalLoading: true, fontLinkModalResult: null });

    const localState = getState();
    const state = {
      pluginToolbarsById: getPluginToolbars(),
    };
    const usedFonts = extractFontsFromState(state);
    const { pendingFontLinks, failedFontLinks = [] } = localState;

    const approvedFontsMap = new Map();
    pendingFontLinks.forEach((fontName) => {
      if (usedFonts.has(fontName)) {
        approvedFontsMap.set(fontName, usedFonts.get(fontName));
      }
    });

    const googleFontsLink = generateGoogleFontsLink(approvedFontsMap);

    if (googleFontsLink) {
      const linkUrl = googleFontsLink.match(/href=["']([^"']+)["']/)?.[1];
      if (linkUrl) {
        try {
          const response = await fetch(linkUrl);
          if (response.ok) {
            const linkElement = document.createElement("link");
            linkElement.rel = "stylesheet";
            linkElement.href = linkUrl;
            linkElement.setAttribute("data-editor-fonts", "true");
            document.head.appendChild(linkElement);

            setState({
              fontLinkModalLoading: false,
              fontLinkModalResult: {
                success: true,
                message: i18n.t("messages.GoogleFontsModal.success") || "Font başarıyla eklendi.",
              },
            });

            setTimeout(() => {
              setState({
                fontLinkModalVisible: false,
                pendingFontLinks: [],
                fontLinkModalResult: null,
              });
            }, 2000);
            return;
          } else {
            // failed to load google font (e.g. 400 Bad Request)
            setState({
              fontLinkModalLoading: false,
              fontLinkModalResult: {
                success: false,
                message:
                  i18n.t("messages.GoogleFontsModal.error") ||
                  "Seçilen fontlar Google Fonts servisinden indirilemedi.",
              },
              failedFontLinks: [...failedFontLinks, ...pendingFontLinks],
            });
            return;
          }
        } catch (e) {
          // fetch error
          setState({
            fontLinkModalLoading: false,
            fontLinkModalResult: {
              success: false,
              message:
                i18n.t("messages.GoogleFontsModal.connectionError") || "Bağlantı hatası oluştu.",
            },
            failedFontLinks: [...failedFontLinks, ...pendingFontLinks],
          });
          return;
        }
      }
    }

    setState({
      fontLinkModalVisible: false,
      pendingFontLinks: [],
      fontLinkModalLoading: false,
    });
  };

  const handleFontLinkModalCancel = () => {
    setState({
      fontLinkModalVisible: false,
      pendingFontLinks: [],
      fontLinkModalResult: null,
    });
  };

  const handleFontModalCancel = () => {
    const { pendingFonts } = getState();

    if (pendingFonts.length > 0) {
      const remainingFonts = pendingFonts.slice(1);
      if (remainingFonts.length > 0) {
        setState({
          fontModalFontName: remainingFonts[0],
          fontModalFontUrl: `https://fonts.google.com/specimen/${remainingFonts[0].replace(/\s+/g, "+")}`,
          pendingFonts: remainingFonts,
        });
      } else {
        setState({
          fontModalVisible: false,
          fontModalFontName: "",
          fontModalFontUrl: "",
          pendingFonts: [],
        });
      }
    } else {
      setState({
        fontModalVisible: false,
        fontModalFontName: "",
        fontModalFontUrl: "",
        pendingFonts: [],
      });
    }
  };

  return {
    loadFonts,
    handleFontLinkModalConfirm,
    handleFontLinkModalCancel,
    handleFontModalCancel,
  };
};

export const createFontLoader = ({ getPluginToolbars, getState, setState }) =>
  buildFontHelpers({ getPluginToolbars, getState, setState });

export const useEditorFonts = ({ pluginToolbars }) => {
  const [fontState, setFontState] = useState({
    fontLinkModalVisible: false,
    pendingFontLinks: [],
    fontModalVisible: false,
    fontModalFontName: "",
    fontModalFontUrl: "",
    pendingFonts: [],
    failedFontLinks: [],
    fontLinkModalLoading: false,
    fontLinkModalResult: null,
  });

  const helpers = useMemo(
    () =>
      buildFontHelpers({
        getPluginToolbars: () => pluginToolbars,
        getState: () => fontState,
        setState: (patch) => setFontState((prev) => ({ ...prev, ...patch })),
      }),
    [pluginToolbars, fontState]
  );

  const loadFonts = useCallback(() => helpers.loadFonts(), [helpers]);
  const handleFontLinkModalConfirm = useCallback(
    () => helpers.handleFontLinkModalConfirm(),
    [helpers]
  );
  const handleFontLinkModalCancel = useCallback(
    () => helpers.handleFontLinkModalCancel(),
    [helpers]
  );
  const handleFontModalCancel = useCallback(() => helpers.handleFontModalCancel(), [helpers]);

  return {
    fontState,
    setFontState,
    loadFonts,
    handleFontLinkModalConfirm,
    handleFontLinkModalCancel,
    handleFontModalCancel,
  };
};
